import { z } from 'zod';
import { zPushNotificationStatus } from './enums';

/**
 * https://docs.expo.dev/push-notifications/sending-notifications/#formats
 */
export const expoPushNotificationSchema = z.object({
  /**
   * An Expo push token or an array of Expo push tokens specifying the recipient(s) of this message.
   * This is the only required field.
   *
   * Expo supports BOTH a single token (string) and multiple (array of tokens).
   * For simplicity, we always use an array.
   */
  to: z.array(z.string()),

  /**
   * (iOS only)
   * When this is set to true, the notification will cause the iOS app to start in the background to run a background task.
   * Your app needs to be configured to support this.
   */
  _contentAvailable: z.boolean().optional(),

  /**
   * A JSON object delivered to your app. It may be up to about 4KiB;
   * the total notification payload sent to Apple and Google must be at most 4KiB or else you will get a "Message Too Big" error.
   */
  data: z.record(z.string(), z.unknown()).optional(),

  /**
   * - The title to display in the notification. Often displayed above the notification body.
   * - Android: Maps to AndroidNotification.title - https://firebase.google.com/docs/reference/fcm/rest/v1/projects.messages#AndroidNotification
   * - iOS: Maps to aps.alert.title. - https://developer.apple.com/documentation/usernotifications/generating-a-remote-notification#Payload-key-reference
   */
  title: z.string().optional(),

  /**
   * - The message to display in the notification. Maps to AndroidNotification.body and aps.alert.body.
   * - Android: Maps to AndroidNotification.body - https://firebase.google.com/docs/reference/fcm/rest/v1/projects.messages#AndroidNotification
   * - iOS: Maps to aps.alert.body. - https://developer.apple.com/documentation/usernotifications/generating-a-remote-notification#Payload-key-reference
   */
  body: z.string().optional(),

  /**
   * Time to Live: the number of seconds for which the message may be kept around for redelivery if it hasn't been delivered yet.
   * Defaults to undefined to use the respective defaults of each provider (1 month for Android/FCM as well as iOS/APNs).
   */
  ttl: z.number().optional(),

  /**
   * Timestamp since the Unix epoch specifying when the message expires.
   * Same effect as ttl (ttl takes precedence over expiration).
   */
  expiration: z.number().optional(),

  /**
   * The delivery priority of the message.
   * Specify default or omit this field to use the default priority on each platform ("normal" on Android and "high" on iOS).
   */
  priority: z
    .union([z.literal('default'), z.literal('normal'), z.literal('high')])
    .default('default'),

  /**
   * (iOS only)
   * The subtitle to display in the notification below the title.
   * Maps to aps.alert.subtitle. https://developer.apple.com/documentation/usernotifications/generating-a-remote-notification#Payload-key-reference
   */
  subtitle: z.string().optional(),

  /**
   * (iOS only)
   * Play a sound when the recipient receives this notification.
   * Specify default to play the device's default notification sound, or omit this field to play no sound.
   * Custom sounds need to be configured via the config plugin and then specified including the file extension.
   * Example: `bells_sound.wav`
   */
  sound: z.string().nullable().optional(),

  /**
   * (iOS only)
   * Number to display in the badge on the app icon.
   * Specify zero to clear the badge.
   */
  badge: z.number().optional(),

  /**
   * (iOS only)
   * The importance and delivery timing of a notification.
   * The string values correspond to the UNNotificationInterruptionLevel enumeration cases.
   * https://developer.apple.com/documentation/usernotifications/unnotificationinterruptionlevel
   */
  interruptionLevel: z
    .union([
      z.literal('active'),
      z.literal('critical'),
      z.literal('passive'),
      z.literal('time-sensitive'),
    ])
    .optional(),

  /**
   * (Android only)
   * ID of the Notification Channel through which to display this notification.
   * If an ID is specified but the corresponding channel does not exist on the device (that has not yet been created by your app),
   * the notification will not be displayed to the user.
   */
  channelId: z.string().optional(),

  /**
   * (Android only)
   * The notification's icon.
   * Name of an Android drawable resource (example: myicon). Defaults to the icon specified in the config plugin.
   */
  icon: z.string().optional(),

  /**
   * (Android only)
   * Currently supports setting a notification image (only on Android).
   * Provide an object with key image and value of type string, which is the image URL.
   */
  richContent: z.any().optional(),

  /**
   * ID of the notification category that this notification is associated with.
   * Find out more about notification categories here - https://docs.expo.dev/versions/latest/sdk/notifications/#manage-notification-categories-interactive-notifications
   */
  categoryId: z.string().optional(),

  /**
   * (iOS only)
   * Specifies whether this notification can be intercepted by the client app. Defaults to `false`.
   * https://developer.apple.com/documentation/usernotifications/modifying-content-in-newly-delivered-notifications?language=objc
   */
  mutableContent: z.boolean().optional(),
});
export type ExpoPushNotification = z.output<typeof expoPushNotificationSchema>;

/**
 * Represents a push notification sent to a single user/device.
 */
export const pushNotificationSchema = z.object({
  /**
   * Assumes that expoPushNotification.to is a single token.
   */
  pushNotificationConfigId: z.string(),
  expoPushNotification: expoPushNotificationSchema,
  /**
   * - `pending`: Initial state, waiting to be processed.
   * - `received`: Expo received the request, by responding with Expo PUSH TICKET with "ok" status.
   * - `sent`: Expo successfully handed off to FCM/APNs service, by responding with Expo PUSH RECEIPT with "ok" status.
   * - `error`: Error occurred while processing.
   * - `timeout`: `ttl` or `expiration` was reached.
   */
  status: zPushNotificationStatus.default('pending'),
  /**
   * Most often the error code from Expo. E.g. "DeviceNotRegistered"
   * https://docs.expo.dev/push-notifications/sending-notifications/#push-ticket-errors
   */
  error: z.string().nullable().default(null),
  /**
   * Most often the detailed error message from Expo.
   * https://docs.expo.dev/push-notifications/sending-notifications/#push-ticket-errors
   */
  errorMessage: z.string().nullable().default(null),
  errorAt: z.number().nullable().default(null),
  /**
   * Since the push notification can be sent to multiple users, we need to know which one caused the error.
   */
  errorExpoPushToken: z.string().nullable().default(null),

  /**
   * The time when the push notification was accepted by Expo.
   */
  acceptedAt: z.number().nullable().default(null),
  expoPushTicketId: z.string().nullable().default(null),
  /**
   * The time when the push notification was sent to FCM/APNs.
   */
  sentAt: z.number().nullable().default(null),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type PushNotification = z.output<typeof pushNotificationSchema>;

export const pushNotificationStrictSchema = pushNotificationSchema;

export const pushNotificationCvxSchema = pushNotificationSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type PushNotificationCvx = z.output<typeof pushNotificationCvxSchema>;
